import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { TruncatedText } from '@/components/commons/truncated-text'
import { Avatar } from '@/components/ui/avatar'
import { DataTable } from '@/components/ui/data-table'
import { CheckCircleFillIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { formatDate } from '@/dayjs'
import { AttendeeNameLink } from '@/features/marquee/components/attendee-name-link'
import { CheckInInlineAction } from '@/features/marquee/components/check-in-inline-action'
import { CheckInRowMenu } from '@/features/marquee/components/check-in-row-menu'
import { CheckInRowSheet } from '@/features/marquee/components/check-in-row-sheet'
import { CheckInStatusBadge } from '@/features/marquee/components/check-in-status-badge'
import { RsvpStatusBadge } from '@/features/marquee/components/rsvp-status-badge'
import { TableScrollBox } from '@/features/marquee/components/table-scroll-box'
import {
	TableEmpty,
	TableError
} from '@/features/marquee/components/table-states'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import {
	allowedCheckInActions,
	attendeeDetailPath,
	type CheckboxMode,
	type CheckInActionGates,
	type CheckInActionId,
	checkInRowActions,
	checkInRowControls,
	childAccountDisplay,
	deriveCheckInBadgeStatus,
	isCheckedIn,
	isLateArrival,
	secondaryCheckInActions,
	segmentLabel
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeAttendee, MarqueeCaptureMode } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** Row + action in flight, so the spinner lands on the button that was clicked. */
export type CheckInPending = { id: string; action: CheckInActionId } | null

interface CheckInListProps {
	data: MarqueeAttendee[]
	/** Event id — enables opening a row's attendee-detail page. */
	eventId?: string
	/** The event's capture mode: only a tune-up has an attendee-detail page. */
	captureMode?: MarqueeCaptureMode
	/**
	 * The event's IANA zone. Arrival times are UTC instants and markets are no
	 * longer all Eastern, so they are rendered in the event's own zone rather
	 * than whatever zone the viewer's browser is in.
	 */
	timeZone?: string
	isLoading?: boolean
	isError?: boolean
	/**
	 * Which action windows the event's state has open: check-in writes while it is
	 * live, roster fixes in every state but `locked`. The two are separate because
	 * the backend answers them in different states, so a closed event still offers
	 * the resolution fixes while offering no way to check anyone in.
	 */
	gates: CheckInActionGates
	/** Dispatches a contextual row action (check-in, undo, no-show, …). */
	onAction: (attendee: MarqueeAttendee, action: CheckInActionId) => void
	/** Row + action in flight, so the spinner lands on the button that was clicked. */
	pending?: CheckInPending
}

export function CheckInList({
	data,
	eventId,
	captureMode,
	timeZone,
	isLoading,
	isError,
	gates,
	onAction,
	pending
}: CheckInListProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')

	// Which row has its menu open, held here rather than inside the menu itself.
	// The action column's cells are rebuilt whenever `pending` changes, which is
	// exactly when an action starts, so a menu owning its own open state would be
	// reset by the very click it is meant to stay open for. One id is enough: only
	// one row's menu can be open at a time.
	const [openMenuId, setOpenMenuId] = useState<string | null>(null)

	// The menu stays up while the write runs so the spinner has somewhere to live;
	// it closes when the action settles, success or failure alike.
	useEffect(() => {
		if (pending === null) {
			setOpenMenuId(null)
		}
	}, [pending])

	const columns = useMemo(
		() => [
			{
				id: 'attendee' as const,
				header: t('marquee:attendee_col_attendee'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<div className='flex min-w-0 flex-col'>
						<AttendeeNameLink
							className='font-medium text-neutral-900 text-sm'
							name={dataItem.name || '-'}
							to={attendeeDetailPath(
								eventId,
								dataItem.id,
								captureMode
							)}
						/>
						{dataItem.company && (
							<TruncatedText className='text-neutral-500 text-sm'>
								{dataItem.company}
							</TruncatedText>
						)}
					</div>
				),
				size: 260
			},
			{
				id: 'childAccount' as const,
				header: t('marquee:attendee_col_child_account'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => {
					const { value, isParent } = childAccountDisplay(dataItem)
					return value ? (
						<div className='flex min-w-0 flex-col'>
							<TruncatedText className='font-medium text-neutral-900 text-sm'>
								{value}
							</TruncatedText>
							{isParent && (
								<TruncatedText className='text-neutral-500 text-sm'>
									{t('marquee:attendee_child_parent')}
								</TruncatedText>
							)}
						</div>
					) : (
						<span className='text-neutral-500 text-sm'>
							{t('marquee:attendee_missing')}
						</span>
					)
				},
				size: 210
			},
			{
				id: 'hostId' as const,
				header: t('marquee:attendee_col_host_id'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<TruncatedText
						className={
							dataItem.airbnb_host_id
								? 'font-medium text-neutral-900 text-sm'
								: 'text-neutral-500 text-sm'
						}
					>
						{dataItem.airbnb_host_id ??
							t('marquee:attendee_missing')}
					</TruncatedText>
				),
				size: 160
			},
			{
				id: 'segment' as const,
				header: t('marquee:attendee_col_segment'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<TruncatedText className='font-medium text-neutral-900 text-sm'>
						{segmentLabel(dataItem.segment)}
					</TruncatedText>
				),
				size: 140
			},
			{
				id: 'rsvpStatus' as const,
				header: t('marquee:attendee_col_rsvp'),
				// Read-only here: the RSVP is data entry, editable in `pre_work`
				// only, and this tab never runs in that state.
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<RsvpStatusBadge status={dataItem.rsvp_status} />
				),
				size: 170
			},
			{
				id: 'status' as const,
				header: t('marquee:attendee_col_status'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<CheckInStatusBadge
						status={deriveCheckInBadgeStatus(dataItem)}
					/>
				),
				size: 200
			},
			{
				id: 'arrivalTime' as const,
				header: t('marquee:checkin_col_arrival_time'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<div className='flex min-w-0 flex-col items-start gap-1'>
						<span className='text-neutral-700 text-sm tabular-nums'>
							{formatDate(
								dataItem.arrival_time,
								'HH:mm',
								timeZone
							)}
						</span>
						{/* Only when the operator stated it. A false reads as
						    "not called late", which is not a claim of being on
						    time, so there is no counterpart badge. */}
						{isLateArrival(dataItem) && (
							<span className='rounded-full bg-orange-600/10 px-2 py-0.5 font-medium text-orange-700 text-xs'>
								{t('marquee:checkin_badge_late')}
							</span>
						)}
					</div>
				),
				size: 150
			},
			{
				id: 'action' as const,
				header: t('common:actions'),
				// Sits over the controls, which are anchored to the table's edge.
				headerAlign: 'end' as const,
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => {
					// The row's state decides which actions exist, the event's
					// state decides which are open: check-in writes are
					// live-only, the resolution fixes answer everywhere but
					// `locked`.
					const { inline, menu } = checkInRowControls(dataItem, gates)
					const rowPending =
						pending?.id === dataItem.id ? pending.action : null

					return (
						<div className='flex w-full items-center justify-end gap-2'>
							<CheckInInlineAction
								action={inline}
								attendee={dataItem}
								onAction={onAction}
								pending={rowPending}
							/>
							<CheckInRowMenu
								actions={menu}
								attendee={dataItem}
								detailPath={attendeeDetailPath(
									eventId,
									dataItem.id,
									captureMode
								)}
								onAction={onAction}
								onOpenChange={(next) =>
									setOpenMenuId(next ? dataItem.id : null)
								}
								open={openMenuId === dataItem.id}
								pending={rowPending}
							/>
						</div>
					)
				},
				// Fits the longest inline label ("Check-in as late arrival")
				// beside the kebab.
				size: 250
			}
		],
		[
			t,
			gates,
			onAction,
			pending,
			openMenuId,
			eventId,
			captureMode,
			timeZone
		]
	)

	if (isLoading || isError) {
		// Desktop: DataTable renders its own loading / error states.
		if (isDesktop) {
			return (
				<TableScrollBox>
					{(scrollParent) => (
						<DataTable
							columns={columns}
							customScrollParent={scrollParent}
							data={[]}
							isError={isError}
							isLoading={isLoading}
						/>
					)}
				</TableScrollBox>
			)
		}
		return isError ? (
			<TableError />
		) : (
			<div className='flex flex-col gap-3'>
				{['s1', 's2', 's3', 's4'].map((key) => (
					<div
						className='h-[90px] animate-pulse rounded-xl bg-grey-100'
						key={key}
					/>
				))}
			</div>
		)
	}

	if (data.length === 0) {
		return <TableEmpty />
	}

	if (!isDesktop) {
		return (
			<div className='flex flex-col gap-3'>
				{data.map((attendee) => (
					<CheckInCard
						attendee={attendee}
						captureMode={captureMode}
						eventId={eventId}
						gates={gates}
						key={attendee.id}
						onAction={onAction}
						onSheetOpenChange={(next) =>
							setOpenMenuId(next ? attendee.id : null)
						}
						pending={
							pending?.id === attendee.id ? pending.action : null
						}
						sheetOpen={openMenuId === attendee.id}
					/>
				))}
			</div>
		)
	}

	return (
		<TableScrollBox>
			{(scrollParent) => (
				<DataTable
					columnPinning={{ left: [], right: ['action'] }}
					columns={columns}
					customScrollParent={scrollParent}
					data={data}
				/>
			)}
		</TableScrollBox>
	)
}

interface CheckInCardProps {
	attendee: MarqueeAttendee
	gates: CheckInActionGates
	eventId?: string
	captureMode?: MarqueeCaptureMode
	onAction: (attendee: MarqueeAttendee, action: CheckInActionId) => void
	/** Which action is in flight for this row, so the spinner lands correctly. */
	pending: CheckInActionId | null
	/** Sheet open state, owned by the list for the same reason the kebab's is. */
	sheetOpen: boolean
	onSheetOpenChange: (open: boolean) => void
}

function CheckInCard({
	attendee,
	gates,
	eventId,
	captureMode,
	onAction,
	pending,
	sheetOpen,
	onSheetOpenChange
}: CheckInCardProps) {
	const { t } = useTranslation()
	const checkedIn = isCheckedIn(attendee)
	const rowActions = checkInRowActions(attendee)
	// The tick already carries the primary action beside the card, so the sheet
	// lists what is left rather than repeating it.
	const menu = allowedCheckInActions(
		secondaryCheckInActions(rowActions),
		gates
	)
	const detailPath = attendeeDetailPath(eventId, attendee.id, captureMode)
	const missingHostId = !attendee.airbnb_host_id
	// Walk-in still waiting for a Salesforce account match at export time.
	const unresolvedWalkIn =
		attendee.is_walk_in && !attendee.sf_child_account_id

	const meta = unresolvedWalkIn
		? t('marquee:checkin_card_unresolved')
		: [attendee.company, attendee.segment && segmentLabel(attendee.segment)]
				.filter(Boolean)
				.join(' · ')

	return (
		<div
			className={cn(
				'flex items-center gap-4 rounded-xl p-4',
				!checkedIn && missingHostId
					? 'border border-grey-300 bg-neutral-white'
					: 'bg-grey-100'
			)}
		>
			<Avatar
				className='border border-neutral-black/[0.08] bg-grey-300 font-medium text-base text-neutral-500'
				imageUrl=''
				name={attendee.name ?? ''}
			/>

			<div className='flex min-w-0 flex-1 flex-col gap-0.5'>
				<div className='flex flex-wrap items-center gap-2'>
					<AttendeeNameLink
						className='min-w-0 font-semibold text-grey-1000 text-sm'
						name={attendee.name || '-'}
						to={detailPath}
					/>
					{attendee.is_walk_in && (
						<span className='shrink-0 rounded-full bg-orange-600/10 px-2 py-0.5 font-medium text-orange-700 text-xs'>
							{t('marquee:checkin_badge_walk_in')}
						</span>
					)}
					{attendee.checkin_status === 'no_show' && (
						<span className='shrink-0 rounded-full bg-error-100 px-2 py-0.5 font-medium text-error-700 text-xs'>
							{t('marquee:checkin_status_no_show')}
						</span>
					)}
					{isLateArrival(attendee) && (
						<span className='shrink-0 rounded-full bg-orange-600/10 px-2 py-0.5 font-medium text-orange-700 text-xs'>
							{t('marquee:checkin_badge_late')}
						</span>
					)}
					{attendee.is_unresolvable && (
						<span className='shrink-0 rounded-full bg-grey-200 px-2 py-0.5 font-medium text-neutral-600 text-xs'>
							{t('marquee:checkin_badge_unresolvable')}
						</span>
					)}
				</div>
				{meta && (
					<span
						className={cn(
							'truncate text-xs',
							unresolvedWalkIn
								? 'text-orange-700'
								: 'text-grey-650'
						)}
					>
						{meta}
					</span>
				)}
				{unresolvedWalkIn && (
					<span className='font-bold text-orange-700 text-xs'>
						{t('marquee:checkin_card_walk_in_hint')}
					</span>
				)}
				{!checkedIn && missingHostId && (
					<span className='font-bold text-red-700 text-xs'>
						{t('marquee:checkin_card_no_host_warning')}
					</span>
				)}
			</div>

			<div className='flex shrink-0 items-center gap-2'>
				{/* Kebab → bottom sheet with the row's secondary actions. While the
				    desk is open it is always there, so a clean row still explains
				    that there is nothing to do; outside that window it appears only
				    when a fix is available, which is how a closed event keeps the
				    resolution actions without reopening the desk. */}
				{(gates.checkin || menu.length > 0 || detailPath) && (
					<CheckInRowSheet
						actions={menu}
						attendee={attendee}
						detailPath={detailPath}
						onAction={onAction}
						onOpenChange={onSheetOpenChange}
						open={sheetOpen}
						pending={pending}
					/>
				)}
				<RowCheckbox
					attendee={attendee}
					canCheckIn={gates.checkin}
					mode={rowActions.checkbox}
					onAction={onAction}
					pending={pending}
				/>
			</div>
		</div>
	)
}

/**
 * The untappable states' slot. Its 24px glyph is the same size the tappable
 * circles draw, but the box around it matches their 44px hit area, so the action
 * column measures the same whatever state the row is in — a no-show sitting
 * beside a waiting row lines up rather than shifting the card's layout. The
 * negative margin is the tappable branches' too: height comes from the card.
 */
function StaticCheckbox({ children }: { children: ReactNode }) {
	return (
		<span className='-my-2.5 flex size-11 shrink-0 items-center justify-center'>
			{children}
		</span>
	)
}

/**
 * Right-hand primary control for a mobile card: a green tick (tap = undo), an
 * empty tappable circle (tap = check-in), or a static disabled circle — driven
 * by {@link checkInRowActions}. Secondary actions live in the kebab sheet.
 */
function RowCheckbox({
	attendee,
	canCheckIn,
	mode,
	onAction,
	pending
}: {
	attendee: MarqueeAttendee
	/** Is the check-in window open (the event is live)? */
	canCheckIn: boolean
	mode: CheckboxMode
	onAction: (attendee: MarqueeAttendee, action: CheckInActionId) => void
	pending: CheckInActionId | null
}) {
	const { t } = useTranslation()

	// Off-hours (not live): show the resting state, no interaction.
	if (!canCheckIn) {
		return (
			<StaticCheckbox>
				{mode === 'undo' ? (
					<CheckCircleFillIcon className='size-6 text-success-600' />
				) : (
					<span className='size-6 rounded-full border-2 border-grey-300' />
				)}
			</StaticCheckbox>
		)
	}

	// No-show, or a row written off as unresolvable: nothing to tap, but the slot
	// still has to measure what the tappable states measure.
	if (mode === 'disabled') {
		return (
			<StaticCheckbox>
				<span className='size-6 rounded-full border-2 border-grey-200 bg-grey-100' />
			</StaticCheckbox>
		)
	}

	if (mode === 'undo') {
		const undoPending = pending === 'undo'
		return (
			<button
				aria-label={t('marquee:checkin_action_undo')}
				className='-my-2.5 flex size-11 shrink-0 items-center justify-center rounded-full transition hover:opacity-80 disabled:opacity-50'
				disabled={undoPending}
				onClick={() => onAction(attendee, 'undo')}
				type='button'
			>
				{undoPending ? (
					<Spinner size={16} />
				) : (
					<CheckCircleFillIcon className='size-6 text-success-600' />
				)}
			</button>
		)
	}

	// mode === 'check_in'
	const checkInPending = pending === 'check_in'
	return (
		<button
			aria-label={t('marquee:checkin_action_check_in')}
			className='group -my-2.5 flex size-11 shrink-0 items-center justify-center rounded-full disabled:opacity-50'
			disabled={checkInPending}
			onClick={() => onAction(attendee, 'check_in')}
			type='button'
		>
			<span className='flex size-6 items-center justify-center rounded-full border-2 border-grey-400 transition group-hover:border-neutral-900'>
				{checkInPending && <Spinner size={14} />}
			</span>
		</button>
	)
}
